import { useTheme } from "@emotion/react";
import { Box, Button } from "@mui/material";
import { tokens } from "../../../theme";
import { useEffect, useState } from "react";
import Loader from "../../global/Loader";
import {pageSizeOptions, pageSize, page} from "../../../utils/paginationConfig";
import { DataGrid } from "@mui/x-data-grid";
import { CustomGridToolbar } from "../../../utils/dataGridCustom";
import {getGeneratedData, getSelectedDateRangeData } from "../../../services/staff-services/bonusService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { useDispatch } from "react-redux";
import { SetCurrentPageTopic } from "../../../store/action/headerChange";
import { saveAs } from "file-saver";
import { escapeCSVField } from "../../../utils/utils";

const Bonus = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const dispatch = useDispatch();
  dispatch(SetCurrentPageTopic("GENERATED RECORDS"));

  const [paginationModel, setPaginationModel] = useState({
    page: page,
    pageSize: pageSize,
  });
  const [rowCount, setRowCount] = useState(0);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [value, setValue] = useState([dayjs(""), dayjs("")]);
  const today = dayjs();

  const headerData = ["Generated Record", "From", "To", "Generated Date"];

  const columns = [
    {
      field: "topic",
      headerName: headerData[0],
      flex: 6,
      renderCell: (params) => (
        <Box style={{ textTransform: "uppercase", cursor: "pointer" }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "fromDate",
      headerName: headerData[1],
      flex: 3,
      cellClassName: "name-column--cell",
    },
    {
      field: "toDate",
      headerName: headerData[2],
      flex: 3,
      cellClassName: "name-column--cell",
    },
    {
      field: "generateDate",
      headerName: headerData[3],
      flex: 3,
      cellClassName: "name-column--cell",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const response = await handleTableData(page, pageSize);
    };
    fetchData();
  }, []);

  const handleTableData = async (
    page = paginationModel.page,
    limit = paginationModel.pageSize
  ) => {
    setIsLoading(true);
    setPaginationModel({ page: page, pageSize: limit });

    try {
      const obj = { page, limit };
      const response = await getGeneratedData(obj);
      setData(response.data.data);
      setRowCount(response.data.totalCount ?? 0);
    } catch (error) {
      console.log(error);
      toast.error("Please refresh");
    }

    setIsLoading(false);
  };

  const handlePaginationModelChange = (newModel) => {
    const resetPage = newModel.pageSize !== paginationModel.pageSize;
    const updatedModel = {
      ...newModel,
      page: resetPage ? 0 : newModel.page,
    };

    setPaginationModel(updatedModel);
    handleTableData(updatedModel.page, updatedModel.pageSize);
  };

  const handleCellClick = (params) => {
    if (params.field === "topic") {
      console.log(params.row.fromDate);
      window.location.href = `salary-details?id=${params.id}&from=${params.row.fromDate}&to=${params.row.toDate}`;
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await getGeneratedData({ page: 0, limit: rowCount });

      const data = response.data.data;
      const csvRows = data.map((row) =>
        [
          escapeCSVField(row.topic),
          escapeCSVField(row.fromDate),
          escapeCSVField(row.toDate),
          escapeCSVField(row.generateDate),
        ].join(",")
      );

      const csvContent = [headerData.join(","), ...csvRows].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `center_bonus_list.csv`);
    } catch (error) {
      console.error("CSV Export Failed:", error);
      toast.error("Failed to export CSV.");
    }
  };

  const handleGenerateBonus = async () => {
    if (value[0] == null && value[1] == null) {
      toast.error("The date range is invalid. Please select a valid range.");
      return;
    }

    const startDateString = value[0].$d;
    const endDateString = value[1].$d;

    if (startDateString == "Invalid Date" || endDateString == "Invalid Date") {
      toast.error("The date range is invalid. Please select a valid range.");
      return;
    }

    setIsLoading(true);

    const startDateObject = new Date(startDateString);
    const startMonth = startDateObject.toLocaleString("default", {
      month: "short",
    });
    const startDay = startDateObject.getDate();
    const startYear = startDateObject.getFullYear();

    const endDateObject = new Date(endDateString);
    const endMonth = endDateObject.toLocaleString("default", {
      month: "short",
    });
    const endDay = endDateObject.getDate();
    const endYear = endDateObject.getFullYear();

    try {
      const response = await getSelectedDateRangeData(
        `${startDay}-${startMonth}-${startYear}`,
        `${endDay}-${endMonth}-${endYear}`
      );

      const result = response.data ?? {};
      if (result.status === "true") {
        setIsLoading(false);
        toast.success(
          result.message ?? "Generated your record. View On the table"
        );
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setIsLoading(false);
        toast.error(
          result.message ?? "No data found for the selected date range."
        );
        return;
      }
    } catch (error) {
      console.log(error);
      toast.error("Please refresh");
    }
  };

  if (isLoading) {
    return <Loader />;
  }
  return (
    <Box m="0 20px">
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ flex: 4 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DateRangePicker", "DateRangePicker"]}>
              <DemoItem component="DateRangePicker">
                <DateRangePicker
                  value={value}
                  onChange={(newValue) => setValue(newValue)}
                  defaultValue={today}
                  disableFuture
                  minDate={dayjs("2024-01-01")}
                  maxDate={dayjs().subtract(1, "day")}
                />
              </DemoItem>
            </DemoContainer>
          </LocalizationProvider>
        </Box>
        <Box sx={{ flex: 2, mt: "6px", ml: "10px" }}>
          <Button
            variant="contained"
            onClick={handleGenerateBonus}
            sx={{ background: "red", height: "50px" }}
          >
            Generate
          </Button>
        </Box>
      </Box>
      <Box
        display="grid"
        width="100%"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            fontSize: "15px",
          },
          "& .name-column--cell": {
            color: colors.primary[100],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            border: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.redAccent[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={data}
          getRowId={(row) => row.id}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          loading={isLoading}
          components={{ Toolbar: CustomGridToolbar }}
          componentsProps={{
            toolbar: {
              onExport: handleExportCSV,
            },
          }}
          onCellClick={handleCellClick}
        />
      </Box>
    </Box>
  );
};

export default Bonus;
